import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ScanBarcode, Search, Minus, Plus, X, Check, Camera, Keyboard, Loader2, Clock, TrendingUp, Star, EyeOff, Eye, Package } from 'lucide-react';
import { searchProducts, getProductByBarcode, logStockOut, getQuickPicks, getSettings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import BarcodeScanner from '../components/BarcodeScanner';
import { useAuth } from '../lib/auth';

const REASONS = [
  { value: 'walk_in_sale', label: 'Walk-in Sale' },
  { value: 'online_sale', label: 'Online Sale' },
  { value: 'sampling', label: 'Sampling' },
  { value: 'breakage', label: 'Breakage' },
  { value: 'other', label: 'Other' },
];

const QUICK_PICKS_HIDDEN_KEY = 'stockops_quick_picks_hidden';

const QuickPickItem = ({ product, onSelect }) => (
  <button
    onClick={() => onSelect(product)}
    className="flex flex-col items-center gap-1 min-w-[64px] p-2 rounded-lg hover:bg-accent active:bg-accent/80 transition-colors"
    data-testid={`quick-pick-${product.sku}`}
  >
    {product.image_url ? (
      <img 
        src={product.image_url} 
        alt={product.name}
        className="w-12 h-12 rounded-lg object-cover bg-muted"
      />
    ) : (
      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
        <ScanBarcode className="w-5 h-5 text-muted-foreground" />
      </div>
    )}
    <span className="text-xs text-center font-medium text-foreground line-clamp-1 max-w-[64px]">
      {product.name.split(' ')[0]}
    </span>
  </button>
);

const QuickPicksSection = ({ title, icon: Icon, items, onSelect, testId }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
      </div>
      <div 
        className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {items.map((product) => (
          <QuickPickItem key={product.id} product={product} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

const StockOutPage = () => {
  const { isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('walk_in_sale');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [quickPicks, setQuickPicks] = useState({ recent: [], top: [], favorites: [] });
  const [loadingQuickPicks, setLoadingQuickPicks] = useState(true);
  const [quickPicksHidden, setQuickPicksHidden] = useState(() => {
    return localStorage.getItem(QUICK_PICKS_HIDDEN_KEY) === 'true';
  });
  const [settings, setSettings] = useState(() => {
    // Load from localStorage if available
    const cached = localStorage.getItem('stockops_settings');
    return cached ? JSON.parse(cached) : { 
      hide_scanner_buttons: false,
      enable_swipe_navigation: false,
    };
  });

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
        // Cache in localStorage
        localStorage.setItem('stockops_settings', JSON.stringify(res.data));
      } catch (error) {
        console.error('Failed to load settings');
      }
    };
    fetchSettings();
  }, []);

  // Fetch quick picks on mount
  useEffect(() => {
    const fetchQuickPicks = async () => {
      try {
        const res = await getQuickPicks();
        setQuickPicks(res.data);
      } catch (error) {
        console.error('Failed to load quick picks');
      } finally {
        setLoadingQuickPicks(false);
      }
    };
    fetchQuickPicks();
  }, []);

  // Toggle quick picks visibility
  const toggleQuickPicks = () => {
    const newValue = !quickPicksHidden;
    setQuickPicksHidden(newValue);
    localStorage.setItem(QUICK_PICKS_HIDDEN_KEY, String(newValue));
  };

  // Search products
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await searchProducts(query);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Handle barcode scan
  const handleBarcodeScan = async (barcode) => {
    setShowScanner(false);
    setShowManualEntry(false);
    
    try {
      const res = await getProductByBarcode(barcode);
      setSelectedProduct(res.data);
      setSearchQuery('');
      setSearchResults([]);
      toast.success(`Found: ${res.data.name}`);
    } catch (error) {
      toast.error('Product not found for this barcode');
    }
  };

  // Handle manual barcode entry
  const handleManualBarcodeSubmit = () => {
    if (manualBarcode.trim()) {
      handleBarcodeScan(manualBarcode.trim());
      setManualBarcode('');
    }
  };

  // Select product from search or quick picks
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Clear selection
  const handleClear = () => {
    setSelectedProduct(null);
    setQuantity(1);
    setReason('walk_in_sale');
    setNote('');
  };

  // Submit stock out
  const handleSubmit = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    setSubmitting(true);
    try {
      await logStockOut({
        product_id: selectedProduct.id,
        quantity,
        reason,
        note: note.trim() || null,
      });
      
      toast.success(`Logged: ${quantity}x ${selectedProduct.name}`);
      handleClear();
      
      // Refresh quick picks after successful log
      try {
        const res = await getQuickPicks();
        setQuickPicks(res.data);
      } catch (e) {
        // Ignore refresh errors
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log stock out');
    } finally {
      setSubmitting(false);
    }
  };

  const hasQuickPicks = quickPicks.recent.length > 0 || quickPicks.top.length > 0 || quickPicks.favorites.length > 0;

  return (
    <div className="space-y-4 animate-enter" data-testid="stock-out-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-heading font-bold">Log Stock Out</h1>
      </div>

      {/* Scanner/Search Section */}
      {!selectedProduct && (
        <>
          {/* Action Buttons - Only show if not hidden in settings */}
          {!settings.hide_scanner_buttons && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => setShowScanner(true)}
                className="h-14 text-base font-semibold rounded-xl bg-primary text-white"
                data-testid="scan-barcode-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan
              </Button>
              <Button
                onClick={() => setShowManualEntry(true)}
                variant="secondary"
                className="h-14 text-base font-semibold rounded-xl"
                data-testid="manual-entry-btn"
              >
                <Keyboard className="w-5 h-5 mr-2" />
                Type Code
              </Button>
            </div>
          )}

          {/* Quick Picks */}
          {!loadingQuickPicks && hasQuickPicks && (
            <div className="bg-muted/30 rounded-xl p-3 -mx-1" data-testid="quick-picks-section">
              {/* Quick Picks Header with Hide Button */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Quick Picks
                </span>
                <button
                  onClick={toggleQuickPicks}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded"
                  data-testid="toggle-quick-picks-btn"
                >
                  {quickPicksHidden ? (
                    <>
                      <Eye className="w-3.5 h-3.5" />
                      <span>Show</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3.5 h-3.5" />
                      <span>Hide</span>
                    </>
                  )}
                </button>
              </div>
              
              {!quickPicksHidden && (
                <>
                  <QuickPicksSection 
                    title="Recent" 
                    icon={Clock} 
                    items={quickPicks.recent} 
                    onSelect={handleSelectProduct}
                    testId="quick-picks-recent"
                  />
                  <QuickPicksSection 
                    title="Top Sellers" 
                    icon={TrendingUp} 
                    items={quickPicks.top} 
                    onSelect={handleSelectProduct}
                    testId="quick-picks-top"
                  />
                  <QuickPicksSection 
                    title="Your Favorites" 
                    icon={Star} 
                    items={quickPicks.favorites} 
                    onSelect={handleSelectProduct}
                    testId="quick-picks-favorites"
                  />
                </>
              )}
            </div>
          )}

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search by SKU or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-bar"
              data-testid="product-search-input"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2" data-testid="search-results">
              {searchResults.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  className="item-card w-full text-left flex items-center gap-3"
                  data-testid={`product-result-${product.sku}`}
                >
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-12 h-12 rounded-lg object-cover bg-muted"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <ScanBarcode className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground font-data">{product.sku}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Selected Product Card */}
      {selectedProduct && (
        <div className="space-y-4">
          {/* Product Info */}
          <div className="item-card relative" data-testid="selected-product">
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 btn-icon w-8 h-8"
              aria-label="Clear selection"
              data-testid="clear-selection-btn"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-4">
              {selectedProduct.image_url ? (
                <img 
                  src={selectedProduct.image_url} 
                  alt={selectedProduct.name}
                  className="w-16 h-16 rounded-xl object-cover bg-muted"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                  <ScanBarcode className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-base">{selectedProduct.name}</h3>
                <p className="text-sm text-muted-foreground font-data">{selectedProduct.sku}</p>
                {selectedProduct.barcode && (
                  <p className="text-xs text-muted-foreground font-data">{selectedProduct.barcode}</p>
                )}
                {/* Admin-only: Show live balance */}
                {isAdmin() && selectedProduct.live_balance !== undefined && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 border border-primary/20">
                    <Package className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      Balance: <span className="font-data">{selectedProduct.live_balance ?? '—'}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quantity Selector */}
          <div className="item-card">
            <label className="text-sm font-medium text-muted-foreground mb-3 block">Quantity</label>
            <div className="qty-stepper justify-center">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="qty-btn"
                data-testid="qty-decrease"
              >
                <Minus className="w-5 h-5" />
              </button>
              <span className="qty-display" data-testid="qty-value">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="qty-btn"
                data-testid="qty-increase"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Reason Selector */}
          <div className="item-card">
            <label className="text-sm font-medium text-muted-foreground mb-3 block">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="input-large" data-testid="reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Note (Optional) */}
          <div className="item-card">
            <label className="text-sm font-medium text-muted-foreground mb-3 block">Note (Optional)</label>
            <Input
              placeholder="Add a note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input-large"
              data-testid="note-input"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary-action"
            data-testid="submit-stock-out"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Logging...</span>
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>Log Stock Out</span>
              </>
            )}
          </Button>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Manual Barcode Entry Modal */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter Barcode/SKU</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Type barcode or SKU..."
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              className="input-large font-data text-center"
              autoFocus
              data-testid="manual-barcode-input"
              onKeyDown={(e) => e.key === 'Enter' && handleManualBarcodeSubmit()}
            />
            <Button
              onClick={handleManualBarcodeSubmit}
              className="btn-primary-action"
              data-testid="manual-barcode-submit"
            >
              <Search className="w-5 h-5" />
              <span>Find Product</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockOutPage;
